import type { FuzzySearchResult } from "@repo/app-types/entities/search";
import { useState, type FC } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import { buildPathForSearchResult, findTopSearchResult } from "./helpers.js";
import { useSearchDialogContext } from "./searchContexts.js";
import { SearchModalOverlay } from "./searchModalOverlay.js";

/**
 * Renders just the search modal (no trigger) for the `/go` deep-link route: it
 * auto-opens with the `?q` query, jumps to the top result once loaded, and
 * returns home when dismissed.
 */
export const NavigationSearchGoRoute: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(true);
  const dialogContext = useSearchDialogContext("go");
  const initialSearchQuery = searchParams.get("q")?.trim();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      navigate(appPaths.home.build());
    }
  };

  const handleSearchResultsLoad = (
    searchResults: readonly FuzzySearchResult[],
  ) => {
    const topSearchResult = findTopSearchResult(searchResults);
    const path = topSearchResult
      ? buildPathForSearchResult(topSearchResult, {
          isTrainingSearchContext: dialogContext.isTrainingSearchContext,
        })
      : undefined;

    if (path) {
      navigate(path);
      setIsOpen(false);
      return;
    }

    // No match for the deep-link query: leave the search open so the user can
    // refine it. Navigating away here would unmount this route and close the
    // search; dismissing it returns home instead (see `handleOpenChange`).
  };

  return (
    <SearchModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      {...dialogContext}
      initialSearchQuery={initialSearchQuery}
      onSearchResultsLoad={handleSearchResultsLoad}
    />
  );
};
